// useScrollHinweis.ts — „Weitere Optionen — scrollen" an langen Auswahllisten.
//
// Befund: Nutzer (Windows + Edge, Overlay-Scrollbalken) merken nicht, dass ein
// Dropdown mehr Einträge hat, als es zeigt — der Scrollbalken erscheint dort
// erst beim Überfahren. Die Liste bekommt deshalb unten einen Hinweis mit
// Verlauf und Chevron, nach dem Scrollen oben den entsprechenden; beide nur
// bei Überlauf, und der untere verschwindet am Ende der Liste.
//
// Umsetzung ohne zusätzliche DOM-Knoten: Diese Datei setzt am Listen-Element
// nur das Attribut `data-scroll-hinweis` ("oben" / "unten" / "oben unten");
// gezeichnet wird per CSS (`styles/scrollHinweis.css`) mit klebenden
// ::before/::after-Elementen. Fremde Knoten in Fluent- oder React-Listen
// einzuhängen würde deren Abgleich stören — ein Attribut stört nichts.
//
// Barrierefreiheit: Der Hinweistext trägt im CSS einen leeren Alternativtext
// (`content: "…" / ""`), Screenreader lesen ihn also nicht zwischen die
// Optionen. Die Optionen bekommen `scroll-margin`, damit die Tastatur-
// Navigation (Fluent und native `scrollIntoView` beachten es) die aktive
// Option nicht unter den Hinweis schiebt. Fokus, Rollen und Tastenbelegung
// bleiben unberührt; der Hinweis nimmt keine Klicks an.
//
// Zwei Wege:
//   - `installiereScrollHinweise()` einmal beim Start (index.tsx): erkennt jede
//     Auswahlliste im Dokument — Fluent Dropdown/Combobox (`.fui-Listbox`),
//     eigene Listen mit role="listbox" (SearchableSelect, MehrfachSelect,
//     BrancheMultiSelect, KontaktSuchfeld …) und `.rd-pop-list` — auch in
//     Portalen, die erst beim Öffnen entstehen.
//   - `useScrollHinweis(ref)` für eine Liste, die keiner dieser Selektoren
//     trifft. Alternativ reicht das Attribut `data-scroll-hinweis-liste`.
// Abschalten je Liste: Attribut `data-scroll-hinweis-aus`.

import * as React from "react";

export const SCROLL_HINWEIS_SELEKTOR =
  '.fui-Listbox, [role="listbox"], .rd-pop-list, [data-scroll-hinweis-liste]';

/** Unter dieser sichtbaren Höhe würde der Hinweis zu viel verdecken. */
const MIN_HOEHE = 90;
/** Toleranz gegen Rundung (Zoom 125 % unter Windows liefert Bruchteile). */
const TOLERANZ = 4;

export interface ScrollHinweisZustand {
  oben: boolean;
  unten: boolean;
}

/** Reine Ableitung aus den Scroll-Maßen — getestet ohne Layout. */
export function scrollHinweisZustand(m: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}): ScrollHinweisZustand {
  const ueberlauf = m.scrollHeight - m.clientHeight > TOLERANZ;
  if (!ueberlauf || m.clientHeight < MIN_HOEHE) return { oben: false, unten: false };
  return {
    oben: m.scrollTop > TOLERANZ,
    unten: m.scrollTop + m.clientHeight < m.scrollHeight - TOLERANZ,
  };
}

const attributWert = (z: ScrollHinweisZustand): string | null =>
  [z.oben ? "oben" : "", z.unten ? "unten" : ""].filter(Boolean).join(" ") || null;

/** Kann das Element überhaupt senkrecht scrollen (und ist es eine Liste)? */
function istScrollListe(el: HTMLElement): boolean {
  if (el.hasAttribute("data-scroll-hinweis-aus")) return false;
  const cs = el.ownerDocument?.defaultView?.getComputedStyle(el);
  if (!cs) return false;
  // Raster (z. B. Seitenübersicht im PDF-Editor) sind keine Auswahllisten.
  if (cs.display === "grid" || cs.display === "inline-grid") return false;
  return cs.overflowY === "auto" || cs.overflowY === "scroll";
}

const angebunden = new WeakMap<HTMLElement, () => void>();

/**
 * Bindet den Hinweis an ein Listen-Element. Liefert die Aufräumfunktion.
 * Mehrfaches Anbinden desselben Elements ist unschädlich.
 */
export function scrollHinweisAnbinden(el: HTMLElement): () => void {
  const vorhanden = angebunden.get(el);
  if (vorhanden) return vorhanden;

  let frame = 0;
  const aktualisieren = () => {
    frame = 0;
    const wert = attributWert(scrollHinweisZustand(el));
    if (wert == null) {
      if (el.hasAttribute("data-scroll-hinweis")) el.removeAttribute("data-scroll-hinweis");
    } else if (el.getAttribute("data-scroll-hinweis") !== wert) {
      el.setAttribute("data-scroll-hinweis", wert);
    }
  };
  const planen = () => {
    if (frame) return;
    const win = el.ownerDocument?.defaultView;
    frame = win?.requestAnimationFrame ? win.requestAnimationFrame(aktualisieren) : (aktualisieren(), 0);
  };

  el.addEventListener("scroll", planen, { passive: true });
  const RO = el.ownerDocument?.defaultView?.ResizeObserver;
  const ro = RO ? new RO(planen) : null;
  ro?.observe(el);
  // Gefilterte Listen (Suchfeld) ändern ihre Höhe über die Kinder.
  const MO = el.ownerDocument?.defaultView?.MutationObserver;
  const mo = MO ? new MO(planen) : null;
  mo?.observe(el, { childList: true, subtree: true });
  aktualisieren();

  const abbauen = () => {
    el.removeEventListener("scroll", planen);
    ro?.disconnect();
    mo?.disconnect();
    if (frame) el.ownerDocument?.defaultView?.cancelAnimationFrame?.(frame);
    el.removeAttribute("data-scroll-hinweis");
    angebunden.delete(el);
  };
  angebunden.set(el, abbauen);
  return abbauen;
}

/** Für eine einzelne Liste, die kein globaler Selektor trifft. */
export function useScrollHinweis<T extends HTMLElement>(ref: React.RefObject<T>, aktiv = true): void {
  React.useEffect(() => {
    const el = ref.current;
    if (!aktiv || !el) return;
    return scrollHinweisAnbinden(el);
  }, [ref, aktiv]);
}

/**
 * Einmal beim Start: bindet jede Auswahlliste im Dokument an, auch solche,
 * die später in einem Portal entstehen (Fluent öffnet Listen in `body`).
 * Liefert eine Abbaufunktion (für Tests).
 */
export function installiereScrollHinweise(doc: Document = document): () => void {
  const win = doc.defaultView;
  if (!win?.MutationObserver || !doc.body) return () => {};
  const aktiv = new Set<HTMLElement>();

  const pruefen = (el: HTMLElement) => {
    if (aktiv.has(el) || !istScrollListe(el)) return;
    aktiv.add(el);
    scrollHinweisAnbinden(el);
  };
  const durchsuchen = (wurzel: ParentNode & Node) => {
    if (wurzel instanceof win.HTMLElement && wurzel.matches(SCROLL_HINWEIS_SELEKTOR)) pruefen(wurzel);
    wurzel.querySelectorAll?.<HTMLElement>(SCROLL_HINWEIS_SELEKTOR).forEach(pruefen);
  };

  let frame = 0;
  const offen = new Set<ParentNode & Node>();
  const geaendert = new Set<Element>();
  const abarbeiten = () => {
    frame = 0;
    for (const el of aktiv) {
      if (!el.isConnected) {
        angebunden.get(el)?.();
        aktiv.delete(el);
      }
    }
    for (const n of offen) if (n.isConnected) durchsuchen(n);
    offen.clear();
    // Bei Attributänderungen nur das Element selbst — kein Teilbaum: Klassen
    // wechseln ständig (Hover, Fokus), ein Durchsuchen wäre teuer.
    for (const el of geaendert) {
      if (el.isConnected && el instanceof win.HTMLElement && el.matches(SCROLL_HINWEIS_SELEKTOR)) pruefen(el);
    }
    geaendert.clear();
  };

  const mo = new win.MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((n) => {
        if (n.nodeType === 1) offen.add(n as Element);
      });
      // Eine Liste, die ihren Stil erst später bekommt (Klasse nachgereicht).
      if (r.type === "attributes" && r.target.nodeType === 1) geaendert.add(r.target as Element);
    }
    if (!frame) frame = win.requestAnimationFrame ? win.requestAnimationFrame(abarbeiten) : (abarbeiten(), 0);
  });
  mo.observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "role"] });
  durchsuchen(doc.body);

  return () => {
    mo.disconnect();
    if (frame) win.cancelAnimationFrame?.(frame);
    for (const el of aktiv) angebunden.get(el)?.();
    aktiv.clear();
  };
}
